import { DealDetail, NegotiationFieldMeta, RepoType } from 'api'
import { Locale, Translation } from 'locale'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { NumericInputField } from 'view/components/inputs'
import { TableColumn, TableOptions } from 'view/components/panels'
import { useAuthorisedUser, useIsAdmin, useUserPreference } from 'view/hooks'
import { DealFieldNames } from 'view/routes/deals/components/DealsGridColumnNames'
import { getDealModificationColour, getNegotiatedValue } from 'view/routes/deals/components/utils'
import { getRateModificationType, getStringModificationType } from 'view/routes/requests/shared/deltaGetters'
import { getFeeFormatOptions, getNotionalFormatOptions, getRateFormatOptions } from 'view/shared/datagrid'
import { OrganisationDecisionMakerSelectboxField } from 'view/shared/entity'
import { isPropertyEmptyInAll } from '../utils'

export const useServicingRerateColumns = (
  onUpdate: (payload: { dealId: string; value: any; field: NegotiationFieldMeta; rawValue?: any }) => void
) => {
  const userPreference = useUserPreference()
  const user = useAuthorisedUser()
  const isAdmin = useIsAdmin()
  const stockBorrowingLendingEnabled = useSelector(isStockBorrowingLendingEnabledSelector)

  const columns: TableColumn<DealDetail>[] = useMemo(
    () => [
      {
        colId: 'dealId',
        headerName: Locale.translate(Translation.TC_DEAL_ID),
        valueGetter: ({ data: { deal } }) => {
          return deal.dealId
        },
        styles: { left: true }
      },
      {
        colId: 'collateral',
        headerName: Locale.translate(Translation.TC_COLLATERAL),
        valueGetter: ({ data: { collateral, collateralBasket } }) => {
          return collateral?.name ?? collateralBasket?.name ?? ''
        }
      },
      {
        colId: 'notional',
        headerName: Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat }),
        valueGetter: ({ data: { deal } }) => {
          return Locale.formatNumber(deal.notional, getNotionalFormatOptions(userPreference.amountFormat, false))
        }
      },
      {
        colId: 'currentRate',
        headerName: Locale.translate(Translation.TC_CURRENT_RATE),
        valueGetter: ({ data: dealDetail }) => {
          return Locale.formatNumber(dealDetail.deal.rate, getRateFormatOptions(dealDetail.referenceRate))
        }
      },
      {
        colId: 'newRate',
        headerName: Locale.translate(Translation.TC_NEW_RATE),
        cellRenderer: ({ data: dealDetail }) => {
          const negotiationFields = dealDetail.servicingSideDetails?.negotiationFields
          const validationIssues = dealDetail.servicingSideDetails?.validationIssues ?? []
          const isRepoTypeStockBorrowing = dealDetail.deal.repoType === RepoType.STOCK_BORROWING_LENDING

          return (
            <NumericInputField
              label={''}
              fieldName={NegotiationFieldMeta.RATE}
              validationField={DealFieldNames.DEAL_RATE}
              formatOptions={getRateFormatOptions(dealDetail.referenceRate)}
              validationIssues={validationIssues}
              underline={getDealModificationColour(
                () => getRateModificationType(negotiationFields?.RATE?.negotiableFieldRequired),
                dealDetail
              )}
              value={getNegotiatedValue(negotiationFields?.RATE?.negotiableFieldRequired)}
              onUpdate={(value, field) => onUpdate({ value, field, dealId: dealDetail.deal.dealId })}
              dataTestId={'ServicingRerateDialog-NewRateNumericInputField'}
              readOnly={isRepoTypeStockBorrowing}
            />
          )
        }
      },
      {
        colId: 'currentFee',
        headerName: Locale.translate(Translation.TC_CURRENT_FEE),
        hide: ({ rows }) => !stockBorrowingLendingEnabled || isPropertyEmptyInAll(rows, dealDetail => dealDetail.deal.fee),
        valueGetter: ({ data: dealDetail }) => {
          return Locale.formatNumber(dealDetail.deal.fee, getFeeFormatOptions())
        }
      },
      {
        colId: 'newFee',
        headerName: Locale.translate(Translation.TC_NEW_FEE),
        hide: ({ rows }) => !stockBorrowingLendingEnabled || isPropertyEmptyInAll(rows, dealDetail => dealDetail.deal.fee),
        cellRenderer: ({ data: dealDetail }) => {
          const negotiationFields = dealDetail.servicingSideDetails?.negotiationFields
          const validationIssues = dealDetail.servicingSideDetails?.validationIssues ?? []
          const isRepoTypeStockBorrowing = dealDetail.deal.repoType === RepoType.STOCK_BORROWING_LENDING

          return (
            <NumericInputField
              label={''}
              fieldName={NegotiationFieldMeta.FEE}
              validationField={DealFieldNames.DEAL_FEE}
              formatOptions={getFeeFormatOptions()}
              validationIssues={validationIssues}
              underline={getDealModificationColour(
                () => getRateModificationType(negotiationFields?.FEE?.negotiableFieldRequired),
                dealDetail
              )}
              value={getNegotiatedValue(negotiationFields?.FEE?.negotiableFieldRequired)}
              onUpdate={(value, field) => onUpdate({ value, field, dealId: dealDetail.deal.dealId })}
              dataTestId={'ServicingRerateDialog-NewFeeNumericInputField'}
              readOnly={!isRepoTypeStockBorrowing}
            />
          )
        }
      },
      {
        colId: 'investmentDecisionMaker',
        headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER_ABBR),
        cellRenderer: ({ data }) => {
          const idmField = data.ownOrder
            ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
            : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER
          const validationIssues = data.servicingSideDetails?.validationIssues ?? []
          const sideFields = data.servicingSideDetails?.sideFields
          const organisationId = isAdmin ? data.deal.initiatorOrganisationId : user.organisationId

          return (
            <OrganisationDecisionMakerSelectboxField
              label=""
              fieldName={idmField}
              value={getNegotiatedValue(sideFields?.[idmField]?.negotiableFieldRequired)}
              onUpdate={(value, field) => onUpdate({ value, field, dealId: data.deal.dealId })}
              validationField={
                data.ownOrder
                  ? DealFieldNames.DEAL_INITIATOR_INVESTMENT_DECISION_MAKER
                  : DealFieldNames.DEAL_RESPONDER_INVESTMENT_DECISION_MAKER
              }
              validationIssues={validationIssues}
              underline={getDealModificationColour(() => getStringModificationType(sideFields?.[idmField]?.negotiableFieldRequired), data)}
              dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Investment'}
              organisationId={organisationId}
            />
          )
        }
      },
      {
        colId: 'executionDecisionMaker',
        headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER_ABBR),
        cellRenderer: ({ data }) => {
          const edmField = data.ownOrder
            ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
            : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER
          const validationIssues = data.servicingSideDetails?.validationIssues ?? []
          const sideFields = data.servicingSideDetails?.sideFields
          const organisationId = isAdmin ? data.deal.initiatorOrganisationId : user.organisationId

          return (
            <OrganisationDecisionMakerSelectboxField
              label=""
              fieldName={edmField}
              value={getNegotiatedValue(sideFields?.[edmField]?.negotiableFieldRequired)}
              onUpdate={(value, field) => onUpdate({ value, field, dealId: data.deal.dealId })}
              validationField={
                data.ownOrder
                  ? DealFieldNames.DEAL_INITIATOR_EXECUTION_DECISION_MAKER
                  : DealFieldNames.DEAL_RESPONDER_EXECUTION_DECISION_MAKER
              }
              validationIssues={validationIssues}
              underline={getDealModificationColour(() => getStringModificationType(sideFields?.[edmField]?.negotiableFieldRequired), data)}
              dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Execution'}
              organisationId={organisationId}
            />
          )
        }
      }
    ],
    [userPreference.amountFormat, stockBorrowingLendingEnabled, onUpdate, isAdmin, user.organisationId]
  )

  return columns
}

export const rerateOptions: TableOptions<DealDetail> = {
  getRowId: row => row.deal.dealId,
  autoExpandRows: true
}
